import { useEffect, useState, type FormEvent } from 'react';
import { Zap, Loader2, ShieldOff, RotateCcw } from 'lucide-react';
import {
  fetchAutochargeSettings, updateAutochargeSettings,
  fetchAutochargeRules, createAutochargeRule, liftAutochargeRule,
  getDisplayErrorMessage,
  type AutochargeRule, type AutochargeRuleKind,
} from '@/lib/api';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './AutochargePage.module.css';
import { Switch } from '@/components/ui/Switch';

/**
 * Plug & Charge (autocharge) — super_admin page. Two things live here:
 *
 *   1. The per-tenant opt-in. Only NEW enrolments read it; an existing
 *      linked car keeps working until the driver unlinks it or a rule
 *      pauses it. Flipping a tenant off is not a kill switch.
 *   2. The model-level kill switch. A vehicle id is the car's controller
 *      MAC, and its stability is an OEM property: when a firmware update
 *      changes it for a whole model line, or a model is found to share one
 *      id across cars, every enrolment for that model goes wrong at once.
 *      A rule suspends every matching linked car in one write; lifting the
 *      rule restores exactly the rows it suspended — never a car the
 *      driver unlinked themselves — so a rule is usable more than once.
 *
 * docs/tickets/ocpp-autocharge-vid.md
 */

const KIND_LABELS: Record<AutochargeRuleKind, string> = {
  make: 'Make',
  make_model: 'Make + model',
  oui: 'OUI (id prefix)',
  tag: 'One vehicle id',
};

const KIND_HINT: Record<AutochargeRuleKind, string> = {
  make: 'Every linked car attached to a saved vehicle of this make, e.g. Rivian.',
  make_model: 'make|model, e.g. Rivian|R1T.',
  oui: 'First six hex digits of the id — the controller-module vendor (e.g. 0c86c7 = Jabil). Coarse: one vendor supplies several makes. Works for cars with no saved vehicle attached.',
  tag: 'The exact id, VID:… — one car, by rule, so the pause is auditable and liftable like the rest.',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function AutochargePage() {
  useRecentRoute('Plug & Charge');
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [savingEnabled, setSavingEnabled] = useState(false);
  const [rules, setRules] = useState<AutochargeRule[] | null>(null);
  const [error, setError] = useState('');
  const [kind, setKind] = useState<AutochargeRuleKind>('make');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [creating, setCreating] = useState(false);
  const [lifting, setLifting] = useState<string | null>(null);
  const [uncoveredNote, setUncoveredNote] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setError('');
    try {
      const [s, r] = await Promise.all([fetchAutochargeSettings(), fetchAutochargeRules()]);
      setEnabled(s.enabled);
      setRules(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  };
  useEffect(() => { void load(); }, []);

  const toggleEnabled = async (next: boolean): Promise<void> => {
    setSavingEnabled(true);
    setError('');
    try {
      const s = await updateAutochargeSettings({ enabled: next });
      setEnabled(s.enabled);
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Could not save the setting.'));
    } finally {
      setSavingEnabled(false);
    }
  };

  const handleCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const created = await createAutochargeRule({ kind, value: value.trim(), reason: reason.trim() });
      const { uncovered_without_vehicle, ...ruleRow } = created;
      setRules((rs) => [ruleRow, ...(rs ?? [])]);
      setUncoveredNote(
        uncovered_without_vehicle != null && uncovered_without_vehicle > 0
          ? `Paused ${created.suspended_count}. ${uncovered_without_vehicle} active linked car${uncovered_without_vehicle === 1 ? ' has' : 's have'} no saved vehicle attached and cannot be reached by a make rule — add an OUI rule for the controller vendor to cover them.`
          : null,
      );
      setValue('');
      setReason('');
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Could not create the rule.'));
    } finally {
      setCreating(false);
    }
  };

  const handleLift = async (rule: AutochargeRule): Promise<void> => {
    setLifting(rule._id);
    setError('');
    try {
      const lifted = await liftAutochargeRule(rule._id);
      setRules((rs) => (rs ?? []).map((r) => (r._id === lifted._id ? lifted : r)));
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Could not lift the rule.'));
    } finally {
      setLifting(null);
    }
  };

  const active = (rules ?? []).filter((r) => r.active);
  const lifted = (rules ?? []).filter((r) => !r.active);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcon}><Zap size={18} /></div>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>Plug &amp; Charge</h1>
          <p className={styles.subtitle}>
            Cars that start a session by announcing their own id at the plug. Enrolment opt-in and the
            model-level pause. super_admin only.
          </p>
        </div>
      </div>

      {error && <div className={styles.alertErr}>{error}</div>}

      <section className={styles.card}>
        <div className={styles.cardTitleRow}>
          <h2 className={styles.cardTitle}>Enrolment</h2>
          <span className={styles.cacheNote}>Server cache TTL: 60s · applies to new links only</span>
        </div>
        <p className={styles.cardLead}>
          When on, a driver who plugs in at one of this tenant&rsquo;s chargers and then starts by app or card is
          offered &ldquo;Link this car?&rdquo; on the charging screen. Off by default. Turning it off does not
          unlink cars already linked — use a rule below for that.
        </p>
        <Switch
          checked={enabled === true}
          disabled={enabled === null || savingEnabled}
          onChange={(e) => void toggleEnabled(e.target.checked)}
          label={<>
            Allow drivers to link their cars at this tenant&rsquo;s chargers
            {savingEnabled && <Loader2 size={13} className={styles.spin} />}
          </>}
        />
      </section>

      <section className={styles.card}>
        <div className={styles.cardTitleRow}>
          <h2 className={styles.cardTitle}>Pause by model</h2>
          <span className={styles.cacheNote}>one write · takes effect at the next plug-in</span>
        </div>
        <p className={styles.cardLead}>
          A rule pauses every linked car it matches and refuses new links for them. Lifting a rule restores
          exactly the cars it paused — a car the driver unlinked in between stays unlinked.
        </p>
        <form className={styles.ruleForm} onSubmit={handleCreate}>
          <div className={styles.ruleFields}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Match on</span>
              <select
                className={formStyles.select}
                value={kind}
                onChange={(e) => setKind(e.target.value as AutochargeRuleKind)}
                disabled={creating}
              >
                {(Object.keys(KIND_LABELS) as AutochargeRuleKind[]).map((k) => (
                  <option key={k} value={k}>{KIND_LABELS[k]}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Value</span>
              <input
                className={formStyles.input}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={kind === 'make_model' ? 'Rivian|R1T' : kind === 'oui' ? '0c86c7' : kind === 'tag' ? 'VID:0c86c701b60b' : 'Rivian'}
                disabled={creating}
                required
              />
            </label>
            <label className={`${styles.field} ${styles.fieldWide}`}>
              <span className={styles.fieldLabel}>Reason (shown to the driver)</span>
              <input
                className={formStyles.input}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Firmware update changed the vehicle id for this model"
                disabled={creating}
                required
                maxLength={500}
              />
            </label>
          </div>
          <p className={styles.hint}>{KIND_HINT[kind]}</p>
          <div className={styles.actions}>
            <button type="submit" className={formStyles.btnPrimary} disabled={creating || !value.trim() || !reason.trim()}>
              {creating ? <><Loader2 size={14} className={styles.spin} /> Pausing…</> : <><ShieldOff size={14} /> Pause matching cars</>}
            </button>
          </div>
        </form>

        {uncoveredNote && <div className={styles.alertWarn}>{uncoveredNote}</div>}
        {rules === null && !error && <p className={styles.loadingText}>Loading…</p>}
        {rules !== null && active.length === 0 && <p className={styles.emptyText}>No active rules.</p>}
        {active.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Match</th><th>Value</th><th>Reason</th><th>Paused</th><th>Since</th><th />
              </tr>
            </thead>
            <tbody>
              {active.map((r) => (
                <tr key={r._id}>
                  <td>{KIND_LABELS[r.kind]}</td>
                  <td className={styles.mono}>{r.value}</td>
                  <td>{r.reason}</td>
                  <td>{r.suspended_count}</td>
                  <td>{formatDate(r.created_at)}</td>
                  <td>
                    <button
                      type="button"
                      className={formStyles.btnSecondary}
                      onClick={() => void handleLift(r)}
                      disabled={lifting === r._id}
                    >
                      {lifting === r._id ? <Loader2 size={13} className={styles.spin} /> : <RotateCcw size={13} />} Lift
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {lifted.length > 0 && (
          <details className={styles.history}>
            <summary>Lifted rules ({lifted.length})</summary>
            <table className={styles.table}>
              <thead>
                <tr><th>Match</th><th>Value</th><th>Reason</th><th>Paused</th><th>Restored</th><th>Lifted</th></tr>
              </thead>
              <tbody>
                {lifted.map((r) => (
                  <tr key={r._id}>
                    <td>{KIND_LABELS[r.kind]}</td>
                    <td className={styles.mono}>{r.value}</td>
                    <td>{r.reason}</td>
                    <td>{r.suspended_count}</td>
                    <td>{r.restored_count ?? '—'}</td>
                    <td>{r.lifted_at ? formatDate(r.lifted_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </section>
    </div>
  );
}
